import type { ClassDefinition } from '../../schemas/classDefinition';
import { magoAdvanced2to5Moves } from '../moves/mago/advanced-2-5';
import { magoAdvanced6to10Moves } from '../moves/mago/advanced-6-10';
import { magoStartingAutoMoves, magoStartingChoiceMoves } from '../moves/mago/initial';

/**
 * Definizione della classe **Mago** (manuale V1, rr. 4000-4394).
 *
 * Quarta classe trascritta. **Prima classe casteggiante**: stress-test del
 * pattern `SpellcastingConfig` (mai popolato finora).
 *
 * Pattern principali stress-testati:
 * - **`spellcasting` definito per la prima volta** (cantripLabel='Trucchetti',
 *   castingMoveId, manaMoveId; nessuna Tradizione Sacra — quelle sono di
 *   Chierico/Paladino/Druido)
 * - **Prima vera applicazione di `MoveSchema.replaces`** (mai usato finora):
 *   *Magia Potenziata Superiore* sostituisce *Magia Potenziata*; *Estremamente
 *   Logico* sostituisce *Logico*
 * - **Famiglio come stub testuale** (FI-8 Seguaci a Ranghi 1-3 in Fase 2):
 *   le 6 peculiarità sono modellate come `MoveSchema.choices` con
 *   `choiceCount: 2`, ma il sistema completo dei Seguaci NON viene introdotto
 *   in questa PR
 * - **Archetipo Umano con scelta-incantesimo parametrica**: la scelta
 *   "incantesimo di 1° livello da Tradizione Sacra del Chierico" resta
 *   descrittiva in description (no `choices` strutturate) finché la spell
 *   list del Chierico non sarà disponibile (PR successiva)
 *
 * **Punti notevoli**:
 * - Dado di danno **d4**, PF base 4, Carico base 7
 * - 5 archetipi razza-tematici (Umano/Elfo/Nano/Halfling/Mezz'orco)
 * - 4 mosse iniziali automatiche + 2 mosse iniziali di scelta (`pickCount: 1`)
 * - 16 mosse avanzate L2-5, 14 mosse avanzate L6-10 (41 mosse totali)
 * - 3 template di Legami (= `maxBonds`, FI-3 rispettato — estremo basso del range)
 * - **Casteggiante**: `spellcasting` definito (Trucchetti, no Tradizioni)
 * - **3 Antitossina** in opzione di equipaggiamento, modellata come Item con
 *   `quantity: 3` (FI-16 risolto)
 */
export const magoClassDefinition: ClassDefinition = {
  id: 'mago',
  name: 'Mago',

  intro:
    "Il mondo è retto da leggi ferree e immutabili. La gravità, per esempio: ciò che cade, cade. La creazione dal nulla? Una menzogna del volgo. E i morti… beh, dovrebbero restare tali. La maggior parte della gente vive e muore entro questi confini inviolabili, accettandoli come destino.\n\nTu, invece, hai osato guardare oltre le porte del conosciuto. Non ti accontenti delle certezze del mondo comune, hai passato notti insonni sui tuoi tomi polverosi, condotto esperimenti che hanno sfiorato la follia e l'eresia. Hai cercato le forze che davvero formano la trama e l'ordito del tessuto della realtà, lasciando che i loro segreti entrassero a far parte del tuo *Corpus Arcano*. Ogni formula che reciti, ogni pagina che studi, ogni mistero che sveli, è un passo in più verso la comprensione profonda di queste leggi supreme.\n\nMa la tua maestria non si ferma alla preparazione meticolosa di formule ancestrali. A volte, la comprensione arcana esplode in intuizione fulminea: plasmi la magia al volo, un'improvvisazione che riscrive la trama dell'istante o si manifesta in una scarica elementale sul campo di battaglia. Altre volte, intessi queste energie in forme durature, dando vita a meraviglie che persistono oltre il momento, siano esse artefatti intrisi di potere ancestrale, luoghi di potere che incanalano l'essenza stessa della magia, o congegni che fondono l'arcano con l'ingegno meccanico in modi sorprendenti.\n\nIl tuo potere va ben oltre la furia distruttiva dei fulmini e delle fiamme in battaglia. È la capacità di difendere l'indifeso con scudi invisibili, di alterare le percezioni altrui, di svelare misteri impenetrabili scrutando la trama invisibile del reale, di piegare la volontà con sussurri arcani, o semplicemente di rendere possibile ciò che la natura nega, manipolando il potere già esistente nel mondo.\n\nLascia che ti osservino con timore, che ti chiamino stregone, negromante, artigiano folle o manipolatore. Tu percorri un sentiero che sfida i confini della realtà, non per romperli senza criterio, ma per padroneggiarli. Chi di loro può dire altrettanto?",

  // Manuale V1, rr. 4015-4019
  suggestedNames: [
    // Umano
    'Avon',
    'Morgana',
    'Rath',
    'Ysolde',
    'Ovid',
    'Vitus',
    'Aldara',
    'Xeno',
    'Uri',
    'Rishol',
    'Ferdia',
    // Elfo
    'Galadiir',
    'Fenfaril',
    'Lilliastre',
    'Phirosalle',
    'Halwyr',
    'Thalad',
    'Rilana',
    'Gwindris',
    'Ewe',
    // Nano
    'Mekrisbi',
    'Indin',
    'Zagtol',
    'Ast',
    'Darmae',
    'Yurrarg',
    'Khazbel',
    'Babra',
    'Inbar',
    'Rinfun',
    // Halfling
    'Asphomar',
    'Merca',
    'Roica',
    'Lichica',
    'Maylard',
    'Rielrosa',
    'Ferumralda',
    'Hylmir',
    // Mezz'orco
    'Uskab',
    'Va',
    'Oggor',
    'Trig',
    'Rongnack',
    'Redbra',
    'Kargga',
    'Amash',
    'Vagri',
    'Zockgar',
  ],

  suggestedTitles: [],

  // Manuale V1, rr. 4021-4027
  aspetti: [
    {
      label: 'Occhi',
      options: ['Tormentati', 'Acuti', 'Folli'],
    },
    {
      label: 'Capelli',
      options: ['Acconciati', 'Selvaggi', 'Cappello a punta'],
    },
    {
      label: 'Veste',
      options: ['Consunta', 'Alla moda', 'Stravagante'],
    },
    {
      label: 'Corpo',
      options: ['Grassoccio', 'Raccapricciante', 'Esile'],
    },
  ],

  // Manuale V1, rr. 4031-4035
  damageDie: 'd4',
  baseHp: 4,
  baseLoad: 7,

  // Manuale V1, rr. 4038-4046
  alignmentPrecepts: {
    buono: 'Usi la magia per aiutare direttamente qualcun altro.',
    legale: "Ripristini l'armonia delle linee di potere magico.",
    neutrale: 'Fai un passo avanti nello svelare un mistero della magia.',
    caotico: "Stravolgi il naturale fluire dell'energia magica nel mondo.",
    malvagio: 'Usi la magia per provocare paura e terrore.',
  },

  // Manuale V1, rr. 4066-4070. **maxBonds: 3** (3 templates ↔ 3, FI-3 rispettato).
  bondTemplates: [
    "_____ ricoprirà un ruolo importante negli eventi a venire. L'ho previsto!",
    '_____ mi sta nascondendo un segreto importante.',
    '_____ è tremendamente disinformato riguardo al mondo: gli insegnerò tutto ciò che posso.',
  ],
  maxBonds: 3,

  // Manuale V1, rr. 4074-4081
  archetypes: [
    {
      id: 'umano',
      name: 'Umano',
      associatedRace: 'umano',
      description:
        'Aggiungi al tuo *Corpus Arcano* un incantesimo di primo livello da qualsiasi Tradizione Sacra del Chierico.',
      grantedMoveId: 'mago.archetipo-umano',
    },
    {
      id: 'elfo',
      name: 'Elfo',
      associatedRace: 'elfo',
      description:
        'Puoi provare a lanciare *Individuazione del Magico* anche se non hai più prese di *Mana*. Inoltre, se scegli di spendere prese come conseguenza di un 7-9, non ne consumerai alcuna.',
      grantedMoveId: 'mago.archetipo-elfo',
    },
    {
      id: 'nano',
      name: 'Nano',
      associatedRace: 'nano',
      description:
        'Quando usi *Scudo Magico*, con 12+, recuperi la presa di *Mana* che hai speso per attivare la mossa.',
      grantedMoveId: 'mago.archetipo-nano',
    },
    {
      id: 'halfling',
      name: 'Halfling',
      associatedRace: 'halfling',
      description:
        'Le illusioni cui dai vita con il trucchetto *Prestidigitazione* sono dettagliate e credibili, ma durano comunque poco tempo.',
      grantedMoveId: 'mago.archetipo-halfling',
    },
    {
      id: 'mezzorco',
      name: "Mezz'orco",
      associatedRace: 'mezzorco',
      description:
        "Quando infliggi danni con un incantesimo, puoi subire 1d4 danni (*ignora armatura*) e aggiungere il risultato al danno inflitto dall'incantesimo che hai lanciato.",
      grantedMoveId: 'mago.archetipo-mezzorco',
    },
  ],

  startingMoves: magoStartingAutoMoves.map((m) => m.id),

  // Manuale V1, rr. 4124-4134: "Inoltre, scegli una delle seguenti mosse"
  startingChoiceGroups: [
    {
      id: 'mossa-iniziale-aggiuntiva',
      name: 'Mossa iniziale aggiuntiva',
      description: 'Scegli una di queste due mosse oltre alle 4 iniziali automatiche.',
      moveOptions: magoStartingChoiceMoves.map((m) => m.id),
      pickCount: 1,
      expandableInAdvanced: false,
    },
  ],

  advancedMoves2to5: magoAdvanced2to5Moves.map((m) => m.id),
  advancedMoves6to10: magoAdvanced6to10Moves.map((m) => m.id),

  // **Prima applicazione di `SpellcastingConfig`** in DWR.
  // Trucchetti = livello 0 incantesimi del Mago (manuale: "tutti i trucchetti
  // della lista del Mago" — Corpus Arcano iniziale).
  // Tradizioni: array vuoto. Il Mago non usa Tradizioni Sacre (quelle sono di
  // Chierico, Paladino, Druido). La spell list del Mago vivrà in
  // `src/data/spells/` come incantesimi con `availableTo` che include
  // `{ classId: 'mago' }` (vedi `SpellAccessSchema`).
  spellcasting: {
    cantripLabel: 'Trucchetti',
    castingMoveId: 'mago.lanciare-incantesimi-arcani',
    manaMoveId: 'mago.mana',
    tradizioni: [],
  },

  // Manuale V1, rr. 4050-4064
  startingEquipment: {
    always: [
      {
        id: 'pozione-curativa-iniziale',
        name: 'Pozione Curativa',
        weight: 0,
        uses: 1,
        tags: [],
      },
      {
        id: 'razioni-da-dungeon',
        name: 'Razioni da Dungeon',
        weight: 1,
        uses: 5,
        tags: ['razione'],
      },
    ],
    choices: [
      {
        id: 'difesa-iniziale',
        prompt: 'Scegli una sola opzione per le tue difese',
        pickCount: 1,
        options: [
          {
            id: 'armatura-di-cuoio-rigido',
            label: 'Armatura di cuoio rigido',
            items: [
              {
                id: 'armatura-di-cuoio-rigido',
                name: 'Armatura di cuoio rigido',
                weight: 1,
                armor: 1,
                tags: ['indossato'],
              },
            ],
          },
          {
            id: 'borsa-di-libri-piu-pozione',
            label: 'Borsa di libri e una Pozione Curativa',
            items: [
              {
                id: 'borsa-di-libri',
                name: 'Borsa di libri',
                weight: 2,
                uses: 5,
                tags: ['lento'],
              },
              {
                id: 'pozione-curativa-da-borsa',
                name: 'Pozione Curativa',
                weight: 0,
                uses: 1,
                tags: [],
              },
            ],
          },
        ],
      },
      {
        id: 'arma-iniziale',
        prompt: 'Scegli la tua arma',
        pickCount: 1,
        options: [
          {
            id: 'pugnale-piu-spada-corta',
            label: 'Pugnale e Spada corta',
            items: [
              {
                id: 'pugnale',
                name: 'Pugnale',
                weight: 1,
                tags: ['corta'],
              },
              {
                id: 'spada-corta',
                name: 'Spada corta',
                weight: 1,
                tags: ['media'],
              },
            ],
          },
          {
            id: 'bastone',
            label: 'Bastone',
            items: [
              {
                id: 'bastone',
                name: 'Bastone',
                weight: 1,
                tags: ['media', 'a due mani'],
              },
            ],
          },
        ],
      },
      {
        id: 'extra-iniziale',
        prompt: "Scegli un'opzione tra le seguenti",
        pickCount: 1,
        options: [
          {
            id: 'pozione-curativa-extra',
            label: 'Pozione Curativa',
            items: [
              {
                id: 'pozione-curativa-extra',
                name: 'Pozione Curativa',
                weight: 0,
                uses: 1,
                tags: [],
              },
            ],
          },
          {
            id: 'antitossina-x3',
            label: '3 Antitossina',
            items: [
              {
                id: 'antitossina',
                name: 'Antitossina',
                weight: 0,
                uses: 1,
                quantity: 3,
                tags: [],
              },
            ],
          },
        ],
      },
    ],
  },
};
